import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { sentryTestFunctions } from "./lib/sentryTest";

// Déterminer l'environnement Sentry
// Priorité : VITE_SENTRY_ENVIRONMENT > import.meta.env.MODE > 'development'
const getSentryEnvironment = (): string => {
  // Si une variable d'environnement explicite est définie, l'utiliser
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
    return import.meta.env.VITE_SENTRY_ENVIRONMENT;
  }
  // Sinon, mapper le mode Vite vers l'environnement Sentry
  const mode = import.meta.env.MODE;
  if (mode === "production") {
    // En production, on peut être sur staging ou production
    // Par défaut, on utilise 'production' mais cela peut être surchargé par VITE_SENTRY_ENVIRONMENT
    return "production";
  }
  return mode; // 'development' par défaut
};

const sentryEnv = getSentryEnvironment();

Sentry.init({
  dsn: "https://0edef41150703d25bf4e0b7840d6ed0e@logging.nereides.weborama.com/48",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Enable logs
  enableLogs: true,
  // Environment: dev, staging, or production
  // Permet de différencier les erreurs selon l'environnement dans Sentry
  environment: sentryEnv,
  // Debug mode en développement pour voir ce qui est envoyé
  debug: import.meta.env.DEV,
  // Traces sample rate (100% en dev pour tester)
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
  // Callback pour voir les événements envoyés
  beforeSend(event, hint) {
    if (import.meta.env.DEV) {
      console.log("📤 Sentry event envoyé:", {
        message: event.message,
        level: event.level,
        environment: event.environment,
        tags: event.tags,
        exception: event.exception,
      });
    }
    return event;
  },
  // Integrations
  integrations: [
    // send console.log, console.warn, and console.error calls as logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    // Browser tracing pour capturer les performances
    Sentry.browserTracingIntegration(),
  ],
});

// Exposer les fonctions de test Sentry sur window pour accès depuis la console
// Uniquement en développement local pour les tests
if (import.meta.env.DEV) {
  (window as any).testSentry = sentryTestFunctions;
  (window as any).Sentry = Sentry;
  console.log(
    "%c🧪 Sentry Test Functions disponibles (dev local uniquement)",
    "color: #36CF7C; font-weight: bold; font-size: 14px;"
  );
  console.log(
    "%cTapez window.testSentry.help() pour voir toutes les fonctions disponibles",
    "color: #36CF7C; font-size: 12px;"
  );
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = createRoot(container);
root.render(<App />);
